<template>
  <div v-if="showLevel" class="benefit-amount">
    <div class="columns is-multiline benefit-metadata">
      <div class="column is-2" v-if="autoBenefit">
        <div class="block">
          <div class="has-text-grey">
            <b-tooltip
              type="is-primary is-light"
              label="Auxiliary benefits are always shown, regardless of filters"
            >
              Aux
              <b-icon icon="information" size="is-small" />
            </b-tooltip>
          </div>
          <strong :class="autoBenefit.type">{{ autoBenefit.label }}</strong>
        </div>
      </div>
      <!-- payment check is required for autobenefits -->
      <div class="column is-2" v-if="benefit.amount">
        <div class="block">
          <div class="has-text-grey">Detail</div>
          <strong class="has-text-primary">{{ benefit.amount }}</strong>
        </div>
      </div>
      <div class="column is-2" v-if="benefit.network">
        <div class="block">
          <div class="has-text-grey">Network</div>
          <strong class="has-text-info">{{ benefit.network }}</strong>
        </div>
      </div>
      <div class="column is-2" v-if="benefit.level">
        <div class="block">
          <div class="has-text-grey">Level</div>
          <span>{{ benefit.level }}</span>
        </div>
      </div>
      <div class="column is-2" v-if="benefit.description">
        <div class="block">
          <div class="has-text-grey">Description:</div>
          <span>{{ benefit.description }}</span>
        </div>
      </div>
      <div class="column is-2" v-if="benefit.auth_required_unknown">
        <div class="has-text-grey">Auth</div>
        <span>Unknown</span>
      </div>
      <div class="column is-2" v-if="benefit.auth_required_unknown === false">
        <div class="block">
          <div v-if="benefit.auth_required !== null">
            <div class="has-text-grey">Auth</div>
            <span v-if="benefit.auth_required === false" class="has-text-grey">Not Required</span>
            <strong v-if="benefit.auth_required === true" class="has-text-danger strong">
              Required
            </strong>
          </div>
        </div>
      </div>
      <div class="column is-2" v-if="benefit.benefit_start">
        <div class="block">
          <div class="has-text-grey">Benefit Begin:</div>
          <span>{{ benefit.benefit_start | formatDate }}</span>
        </div>
      </div>
      <div class="column is-2" v-if="benefit.benefit_end">
        <div class="block">
          <div class="has-text-grey">Benefit End:</div>
          <span>{{ benefit.benefit_end | formatDate }}</span>
        </div>
      </div>
      <div class="column" v-if="benefit.coverage_start">
        <div class="has-text-grey">Coverage Begin</div>
        <span>{{ benefit.coverage_start | formatDate }}</span>
      </div>
      <div class="column" v-if="benefit.coverage_end">
        <div class="has-text-grey">Coverage End</div>
        <span>{{ benefit.coverage_end | formatDate }}</span>
      </div>
      <div class="column" v-if="benefit.insurance_type">
        <div class="has-text-grey">Insurance Type</div>
        <span>{{ benefit.insurance_type }}</span>
      </div>
      <div class="column" v-if="benefit.plan_number">
        <div class="has-text-grey">Plan Number</div>
        <span>{{ benefit.plan_number }}</span>
      </div>
      <div class="column" v-if="benefit.plan_network_name || benefit.plan_network_id">
        <div class="has-text-grey">Plan Network</div>
        <span>{{ benefit.plan_network_name }}{{ benefit.plan_network_name && benefit.plan_network_id ? ' · ' : '' }}{{ benefit.plan_network_id }}</span>
      </div>
      <div class="column is-3" v-if="benefit.place_of_service">
        <div class="block">
          <div class="has-text-grey">Place of service:</div>
          <span>{{ benefit.place_of_service }}</span>
        </div>
      </div>
    </div>

    <BenefitText
      :merged="benefit.merged_benefit_texts"
      :text="benefit.text"
      :notes="benefit.payer_notes"
      :plan-notes="planNotes"
      :accumulation="['deductibles', 'outOfPocket'].includes(benefit.amount_code)"
    />

    <ContactList :contacts="benefit.contacts" />

    <div v-if="deliveryInformation.length">
      <div class="has-text-grey">Delivery Information</div>
      <div v-for="(information, index) in deliveryInformation" :key="index" class="mb-2">
        <div v-for="row in information" :key="row.label">
          <strong>{{ row.label }}:</strong>
          {{ row.value }}
        </div>
      </div>
    </div>
    <hr />
  </div>
</template>

<script>
import BenefitText from './BenefitText';
import ContactList from './ContactList';

export default {
  name: 'BenefitAmount',
  components: {
    BenefitText,
    ContactList,
  },
  props: {
    benefit: Object,
    autoBenefit: Object,
    selectedLevel: String,
    planNotes: Array,
  },
  computed: {
    deliveryInformation() {
      return (this.benefit.delivery_information || [])
        .map((information) =>
          [
            ['Quantity', information.quantity],
            [
              information.quantity_qualifier ? 'Unit' : 'Unit Code',
              information.quantity_qualifier || information.quantity_qualifier_code,
            ],
            ['Amount', information.amount],
            [information.per ? 'Per' : 'Per Code', information.per || information.per_code],
            [
              information.time_period ? 'Time Period' : 'Time Period Code',
              information.time_period || information.time_period_code,
            ],
            ['Number of Periods', information.time_periods],
            [
              information.pattern ? 'Pattern' : 'Pattern Code',
              information.pattern || information.pattern_code,
            ],
            [information.time ? 'Time' : 'Time Code', information.time || information.time_code],
          ]
            .map(([label, value]) => ({
              label,
              value: value === null || value === undefined ? '' : value.toString().trim(),
            }))
            .filter((row) => row.value)
        )
        .filter((information) => information.length);
    },
    showLevel() {
      return ['All', this.benefit.level].includes(this.selectedLevel);
    },
  },
};
</script>

<style scoped>
.benefit-metadata { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 0.75rem 1rem; margin: 0; }
.benefit-metadata > .column { padding: 0; width: auto; min-width: 0; font-size: 0.9rem; overflow-wrap: anywhere; }
.benefit-amount { overflow-wrap: anywhere; }

pre {
  white-space: pre-wrap;
  word-wrap: anywhere;
}
/* There are some payer notes that have no spaces and are very long which never break. */
.force-break {
  overflow-wrap: break-word;
}

hr {
  margin: 1.5rem 0;
}

.content ul {
  margin-top: 0;
}
</style>
